<template>
    <div class="option-list">
        <label class="form-group" :class="classes" v-if="optionList"
                @mouseover="$emit('focus')" @mouseout="$emit('blur')">
            <span class="control-label" v-if="optionList.title">
                <span v-html="optionList.title"></span>
            </span>
            <div class="notification-container" v-if="displayNotification">
                <span class="input-tip">{{ notificationContent }}</span>
            </div>
            <div class="form-control" ref="listGroup" :class="{ 'image-group': withImages, 'list-group': !withImages }">
                <a role="button" v-for="(option, index) in optionList.options" :key="option.value"
                    class="list-group-item" :class="getCssClasses(option, index + 1)"
                    @blur="$emit('blur')"
                    @focus="$emit('focus')"
                    @click.prevent="setValue(option)">
                    <div class="container" v-on:mouseenter="hovered = option.value" v-on:mouseleave="hovered = ''">
                        <div class="top">
                            <div class="disabled-info" v-if="option.disabled && (option.disableText || disabledInfoText) && !option.messages?.length">
                                <span class="icon-exclamation-circle"></span>
                                <span>{{ option.disableText ?? disabledInfoText }}</span>
                            </div>
                            <div class="image" :class="[{ [`scaling-${imageScaling}`]: true, 'zoomed': hovered == option.value }]" v-if="option.image" :style="{ 'background-image': ui.getBackgroundImageUrl(option.image, 600)! }"></div>

                            <h3>
                                {{ option.text }}
                            </h3>

                            <div class="preview-button" v-if="enableZoom" @click.stop="openPopup(option)">
                                {{ ui.t("worktopMaterialPreviewLinkText") }}
                            </div>
                        </div>
                        
                        <div class="messages" v-if="option.messages.length > 0 && withImages">
                            <div v-for="message in option.messages" :key="message" v-html="ui.t(message)"></div>
                        </div>

                        <div class="warnings" v-if="warningsVisible">
                            <span class="input-tip" v-for="warning in option.warnings" :key="warning" v-html="ui.t(warning)"></span>
                        </div>
                    </div>
                </a>
            </div>
        </label>
    </div>
</template>

<script async setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue';
import type { EmbedMedia, OptionList, Option } from '../models';
import { useState, useUI } from '../store';
import axios from 'axios';


const { 
    optionList,
    modelValue,
    multiple,
    withImages,
    linkText,
    classes,
    enableZoom,
    itemClasses,
    disabledInfoText,
    imageScaling = "none",
    valueChangedText
 } = defineProps<{ 
    optionList: OptionList,
    modelValue: string | string[],
    multiple: boolean,
    withImages: boolean,
    linkText: string,
    classes: string,
    enableZoom: string,
    itemClasses: string[],
    disabledInfoText: string,
    imageScaling: "none" | "full",
    valueChangedText?: string,
 }>();

const emit = defineEmits(["input", "focus", "blur", "update:modelValue", "selected", "item-clicked"]);
 
const youtubeRegex = /^.*(?:youtu.be\/|v\/|u\/\w+\/|embed\/|v=)([^#\&\?]*).*/;
const vimeoRegex = /(?:http|https)?:\/\/(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|video\/|)(\d+)(?:|\/\?)/;

const itemsPerRow = ref<number | null>(null);
const warningsVisible = ref(false);
const hovered = ref("");
let warningTimer: any = null;

const listGroup = useTemplateRef("listGroup")
const lastValue = ref();

const displayNotification = ref(false);
const notificationContent = ref("");
let notificationTimeoutHandle: any = null;

const { state } = useState();
const { ui } = useUI();

const valueChangeWatch = watch(() => modelValue, 
 () => {
    if (modelValue != lastValue.value) {
        clearTimeout(notificationTimeoutHandle);
        notificationContent.value = valueChangedText ?? "";
        displayNotification.value = true;
        notificationTimeoutHandle = setTimeout(() => {
            displayNotification.value = false;
        }, 4000)
    }
 }
);

function mounted() {
    window.addEventListener("resize", ensureItemsPerRow);

    ensureItemsPerRow();
}

const openPopup = async (option: Option) => {
    var media = await getEmbedMedia(option);

    ui.openEmbedPopup(media);
}

const getEmbedMedia = async (option: Option): Promise<EmbedMedia[]> => {
    let mediaCollection: EmbedMedia[] = [];

    if (enableZoom) {
        for (var mediaUrl of option.embedMedia) {                
            let youtubeEmbed = mediaUrl.match(youtubeRegex);

            if (youtubeEmbed) {
                mediaCollection.push({
                    previewImage: "https://i.ytimg.com/vi/" + youtubeEmbed[1]  + "/hq720.jpg",
                    embedCode: '<iframe width="560" height="315" src="https://www.youtube.com/embed/' + youtubeEmbed[1]  + '" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>' 
                });

                continue;
            }

            let vimeoEmbed = mediaUrl.match(vimeoRegex);

            if (vimeoEmbed) {
                let { data } = await axios.get<any>("https://vimeo.com/api/oembed.json?url=" + mediaUrl);

                mediaCollection.push({
                    previewImage: data && data.thumbnail_url ? data.thumbnail_url as string : null,
                    embedCode: '<div style="height: 100%;position:relative;" class="vimeo">'+ data.html +'</div>'
                });

                continue;
            }

            mediaCollection.push({
                previewImage: mediaUrl,
                embedCode: null
            });
        }            
    }
            
    return mediaCollection;
}

function ensureItemsPerRow() {
    let container = listGroup.value as HTMLDivElement;
    let previousTop = 0;

    if (!container || !container.children)
        return;

    let index = 1;

    for (let child of Array.from(container.children)) {
        let boundingRect = child.getBoundingClientRect();

        if (previousTop != 0 && previousTop != boundingRect.top) {
            break;
        }

        previousTop = boundingRect.top;

        itemsPerRow.value = index;

        index++;
    }
}

function getCssClasses(option: Option, index: number) {
    let classes = [ ];

    if (option.disabled)
        classes.push("disabled");

    if (isActive(option))
        classes.push("active");

    if (enableZoom)
        classes.push("with-preview");

    if (index % itemsPerRow.value! == 0 || index == optionList.options.length)
        classes.push("row-last");

    if ((index - 1) % itemsPerRow.value! == 0)
        classes.push("row-first");

    if (itemClasses)
        classes = [ ...classes, ...itemClasses ];

    return classes;
}

function isActive(option: Option) {
    if (!modelValue)
        return false;

    return multiple
        ? (modelValue as string[]).indexOf(option.value.toString()) >= 0
        : modelValue == option.value;
}

function setValue(option: Option) {
    emit("item-clicked", { value: option });

    if (option.disabled)
        return;

    state.dirty = true;

    if (multiple) {
        let values = modelValue as string[] || [];
        let index = values.indexOf(option.value.toString());

        if (index !== -1) {
            values.splice(index, 1);
        }
        else {
            values.push(option.value.toString());

            showWarning(option);
        }
        emit("update:modelValue", values);
    }
    else {
        emit("update:modelValue", option.value);

        showWarning(option);
    }

    lastValue.value = option.value;

    emit("selected", option.value);
}

function showWarning(option: Option) {
    clearTimeout(warningTimer);

    warningsVisible.value = option.warnings.length > 0 && withImages;

    if(warningsVisible.value) {
        // let self = this;

        warningTimer = setTimeout(function() {
            // self.warningsVisible = false;
            warningsVisible.value = false;
        }, 4000);
    }
}

mounted();

</script>
